import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import BasicTile from 'src/designSystem/components/molecules/BasicTile/BasicTile'
import Button from 'src/designSystem/components/atoms/Button/Button'
import Box from 'src/designSystem/components/atoms/Box/Box'
import {NamedColors} from 'src/designSystem/colors'
import {SvgIcon} from 'src/designSystem/components/atoms/SvgIcon/SvgIcon'
import {
  SubscriptionStatus,
  SubscriptionSubstatus,
} from '@possible/cassandra/src/types/types.mobile.generated'
import {DividerWithTextOrIcon} from 'src/designSystem/components/atoms/DividerWithTextOrIcon/DividerWithTextOrIcon'

export type MembershipOverdueTileProps = {
  amount: number
  testID?: string
  onPayMembership: () => void
  subscriptionStatus?: SubscriptionStatus
  subscriptionSubStatus?: SubscriptionSubstatus
}

export type MembershipOverdueTileData = {
  title: string
  description: string
}

/**
 * A tile to inform the user that their membership payment is overdue.
 * Includes an action button to pay the membership fee.
 * @example <MembershipOverdueTile amount={15} onPayMembership={() => foo()} membershipStatus={SubscriptionStatus.Cancelled} membershipSubStatus={SubscriptionSubstatus.CancelledPaused}/>
 */
export const MembershipOverdueTile: React.FC<MembershipOverdueTileProps> = ({
  amount,
  testID,
  onPayMembership: handleOnPayMembership,
  subscriptionStatus,
  subscriptionSubStatus,
}: MembershipOverdueTileProps) => {
  const {t} = useTranslation('DashboardMembershipOverdueTile')

  const isMembershipCancelledAndPaused =
    subscriptionStatus === SubscriptionStatus.Cancelled &&
    subscriptionSubStatus === SubscriptionSubstatus.CancelledPaused

  const isMembershipCancelled =
    subscriptionStatus === SubscriptionStatus.Cancelled &&
    subscriptionSubStatus === SubscriptionSubstatus.Cancelled

  const tileData: MembershipOverdueTileData = useMemo(() => {
    if (isMembershipCancelledAndPaused) {
      return {
        title: t('MembershipIsPaused'),
        description: t('PayNowToReActivate'),
      }
    }

    if (isMembershipCancelled) {
      return {
        title: t('MembershipOverdue'),
        description: t('PleasePayTheAmountYouOwe', {amount}),
      }
    }

    return {
      title: t('MembershipOverdue'),
      description: t('PayNowToStayCurrent'),
    }
  }, [amount, isMembershipCancelled, isMembershipCancelledAndPaused, t])

  return (
    <Box gap="small">
      <BasicTile
        padding="medium"
        styles={{padding: 'medium'}}
        align="center"
        testID={testID}
        gap="small"
      >
        <Box fill="horizontal" align="start" gap="little">
          <SvgIcon
            name="warning"
            isFilled
            colorVariant={
              isMembershipCancelledAndPaused || isMembershipCancelled ? 'error' : 'warning'
            }
            size="medium"
            testID="Warning-Icon"
          />
          <PFText variant="p_lg_semibold">{tileData.title}</PFText>
          <PFText variant="p_semibold">
            {t('Overdue', {
              amount,
            })}
          </PFText>
          <PFText variant="p">{tileData.description}</PFText>
        </Box>
        <Box align="center" gap="small" fill="horizontal">
          <Button
            mode="primary"
            size="medium"
            onPress={() => {
              handleOnPayMembership()
            }}
            width="100%"
            testID={`${testID}-PayMembershipBtn`}
          >
            {t('PayMembership')}
          </Button>
        </Box>
        {isMembershipCancelled ? (
          <Box fill="horizontal" gap="small">
            <DividerWithTextOrIcon hasNoTextNoIcon />
            <Box gap="little">
              <PFText variant="p_semibold">{t('YourMembershipIsCancelled')}</PFText>
              <PFText variant="p" color={NamedColors.SILVER}>
                {t('InOrderToResumeYourMembership')}
              </PFText>
            </Box>
          </Box>
        ) : null}
      </BasicTile>
    </Box>
  )
}
